import React, { useState } from "react";
import Image from "../../../components/AppImage";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useTranslation } from "react-i18next";

const WishlistProductCard = ({
  product,
  viewMode,
  isSelectionMode,
  isSelected,
  onToggleSelection,
  onRemoveFromWishlist,
  onAddToCart,
  onProductClick,
}) => {
  const { t, i18n } = useTranslation();

  const [isRemoving, setIsRemoving] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const handleRemove = async () => {
    setIsRemoving(true);
    await onRemoveFromWishlist(product.id);
    setIsRemoving(false);
  };

  const handleAddToCart = async () => {
    setIsAddingToCart(true);
    await onAddToCart(product);
    setIsAddingToCart(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(i18n.language === "en" ? "en" : "ar", {
      style: "currency",
      currency: "IQD",
    }).format(price);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={14}
        className={
          index < Math.floor(rating)
            ? "text-yellow-400 fill-current"
            : "text-gray-300"
        }
      />
    ));
  };

  // Extracting the relevant product details
  const {
    name,
    desc: description,
    price,
    price_after_discount: salePrice,
    variants,
    in_favorite: inFavorite,
    rate: rating,
    reviews: reviewCount,
    images,
  } = product;

  // Assuming the first variant is the one to display
  const firstVariant = variants[0] || {};
  const inStock = firstVariant.quantity > 0;

  if (viewMode === "list") {
    return (
      <div className="bg-card border border-border rounded-lg p-4 hover:shadow-soft-hover transition-smooth">
        <div className="flex gap-4">
          {/* Selection Checkbox */}
          {isSelectionMode && (
            <div className="flex-shrink-0 pt-2">
              <button
                onClick={() => onToggleSelection(product.id)}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-smooth ${
                  isSelected
                    ? "bg-primary border-primary text-white"
                    : "border-border hover:border-primary"
                }`}
              >
                {isSelected && <Icon name="Check" size={12} />}
              </button>
            </div>
          )}

          {/* Product Image */}
          <div className="flex-shrink-0">
            <div className="w-24 h-24 bg-muted rounded-lg overflow-hidden">
              <Image
                src={images[0]?.file_path} // Use the first image
                alt={name}
                className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-smooth"
                onClick={() => onProductClick(product.id)}
              />
            </div>
          </div>

          {/* Product Details */}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start mb-2">
              <h3
                className="font-semibold text-text-primary cursor-pointer hover:text-primary transition-smooth line-clamp-2"
                onClick={() => onProductClick(product.id)}
              >
                {name}
              </h3>

              {/* Remove Button */}
              <button
                onClick={handleRemove}
                disabled={isRemoving}
                className="flex-shrink-0 p-1 text-text-secondary hover:text-error transition-smooth"
              >
                <Icon
                  name={isRemoving ? "Loader2" : "Heart"}
                  size={20}
                  className={`${
                    isRemoving ? "animate-spin" : "fill-current text-accent"
                  }`}
                />
              </button>
            </div>

            <p className="text-sm text-text-secondary mb-2 line-clamp-2">
              {description}
            </p>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-1">
                {renderStars(rating)}
              </div>
              <span className="text-sm text-text-secondary">
                ({reviewCount})
              </span>
            </div>

            {/* Price and Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-text-primary">
                  {formatPrice(salePrice || price)}
                </span>
                {salePrice && (
                  <>
                    <span className="text-sm text-text-secondary line-through">
                      {formatPrice(price)}
                    </span>
                    <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded-full">
                      -{Math.round(((price - salePrice) / price) * 100)}%
                    </span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Add to Cart */}
                <Button
                  size="sm"
                  disabled={!inStock || isAddingToCart}
                  loading={isAddingToCart}
                  iconName="ShoppingCart"
                  iconPosition="left"
                  onClick={handleAddToCart}
                >
                  {t("addToCart")}
                </Button>
              </div>
            </div>

            {/* Added Date */}
            <p className="text-xs text-text-secondary mt-2">
              {t("added")} {new Date().toLocaleDateString()}{" "}
              {/* Placeholder for added date */}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-soft-hover transition-smooth group">
      {/* Selection Checkbox */}
      {isSelectionMode && (
        <div className="absolute top-3 left-3 z-10">
          <button
            onClick={() => onToggleSelection(product.id)}
            className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-smooth ${
              isSelected
                ? "bg-primary border-primary text-white"
                : "bg-white border-border hover:border-primary shadow-soft"
            }`}
          >
            {isSelected && <Icon name="Check" size={14} />}
          </button>
        </div>
      )}

      {/* Product Image */}
      <div className="relative aspect-square bg-muted overflow-hidden">
        <Image
          src={images[0]?.file_path} // Use the first image
          alt={name}
          className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-smooth"
          onClick={() => onProductClick(product.id)}
        />

        {/* Price Drop Badge */}
        {salePrice && (
          <div className="absolute top-3 right-3 bg-accent text-accent-foreground text-xs font-medium px-2 py-1 rounded-full">
            -{Math.round(((price - salePrice) / price) * 100)}%
          </div>
        )}

        {/* Remove from Wishlist */}
        <button
          onClick={handleRemove}
          disabled={isRemoving}
          className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-white rounded-full shadow-soft opacity-0 group-hover:opacity-100 transition-smooth"
          style={{ right: salePrice ? "3.5rem" : "0.75rem" }}
        >
          <Icon
            name={isRemoving ? "Loader2" : "Heart"}
            size={16}
            className={`${
              isRemoving
                ? "animate-spin text-text-secondary"
                : "fill-current text-accent"
            }`}
          />
        </button>
      </div>

      {/* Product Info */}
      <div className="p-4">
        <h3
          className="font-semibold text-text-primary mb-2 line-clamp-2 cursor-pointer hover:text-primary transition-smooth"
          onClick={() => onProductClick(product.id)}
        >
          {name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-1">{renderStars(rating)}</div>
          <span className="text-sm text-text-secondary">({reviewCount})</span>
        </div>

        {/* Price */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg font-bold text-text-primary">
            {formatPrice(salePrice || price)}
          </span>
          {salePrice && (
            <span className="text-sm text-text-secondary line-through">
              {formatPrice(price)}
            </span>
          )}
        </div>

        {/* Stock Status */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-text-secondary">
            {t("added")} {new Date().toLocaleDateString()}{" "}
            {/* Placeholder for added date */}
          </span>
        </div>

        {/* Add to Cart Button */}
        <Button
          size="sm"
          fullWidth
          disabled={!inStock || isAddingToCart}
          loading={isAddingToCart}
          iconName="ShoppingCart"
          iconPosition="left"
          onClick={handleAddToCart}
        >
          {t("addToCart")}
        </Button>
      </div>
    </div>
  );
};

export default WishlistProductCard;
